/**
 * AppShell: left workspace column (header with brand, tab strip, notifications bell, feedback and account,
 * then one always-mounted pane per tab, Home when there are none) + right CEPI column
 * (the shell's one chat panel).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import FeedbackButton from '../components/FeedbackButton';
import UnifiedChatPanel from '../components/UnifiedChatPanel';
import UserProfile from '../components/UserProfile';
import DetachedRouter from './DetachedRouter';
import CapPrompt from './CapPrompt';
import Home from './Home';
import NotificationsBell from './NotificationsBell';
import { isShellPath, SHELL_ROUTES } from './shellRoutes';
import { useShell } from './shellContext';
import { TabLocationSync } from './TabLocationSync';
import { TabProvider } from './TabContext';
import TabStrip from './TabStrip';

const LAYOUT_KEY = 'ld.shell.layout.v1';
const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 300;
const MAX_WIDTH = 640;
const RAIL_WIDTH = 64;

const clampWidth = (w: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

interface Layout { width: number; collapsed: boolean }

function readLayout(): Layout {
  try {
    const raw = JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? 'null');
    if (raw && typeof raw === 'object') {
      return {
        width: typeof raw.width === 'number' ? clampWidth(raw.width) : DEFAULT_WIDTH,
        collapsed: raw.collapsed === true,
      };
    }
  } catch { /* unreadable storage: default layout */ }
  return { width: DEFAULT_WIDTH, collapsed: false };
}

function writeLayout(l: Layout): void {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(l));
  } catch { /* the layout just will not persist */ }
}

/** Below `lg` (1024px) the chat and workspace share the screen via a toggle. */
function useNarrow(): boolean {
  const query = '(max-width: 1023px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

/**
 * Link interceptor for one tab. Capture: a modified click on an in-app link opens
 * a tab. Bubble: any other plain click on a shell link navigates this tab's own
 * router instead of reloading the page. Lives inside the tab's router for `useNavigate`.
 */
const TabBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { openTab } = useShell();
  const navigate = useNavigate();

  const shellPathOf = (e: React.MouseEvent): { path: string; hash: string } | null => {
    const a = (e.target as Element).closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || a.hasAttribute('target') || a.hasAttribute('download')) return null;
    let url: URL;
    try {
      url = new URL(a.getAttribute('href') ?? '', window.location.href);
    } catch {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    const path = url.pathname + url.search;
    // Hash-only and same-page-plus-hash links are in-page anchors: the browser scrolls them.
    if (url.hash && path === window.location.pathname + window.location.search) return null;
    return isShellPath(path) ? { path, hash: url.hash } : null;
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (e.button !== 0 || !(e.metaKey || e.ctrlKey || e.shiftKey)) return;
    const target = shellPathOf(e);
    if (target === null) return;
    e.preventDefault();
    openTab(target.path);
  };
  const onClick = (e: React.MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = shellPathOf(e);
    if (target === null) return;
    e.preventDefault();
    navigate(target.path + target.hash);
  };

  return (
    <div className="h-full" onClickCapture={onClickCapture} onClick={onClick}>
      {children}
    </div>
  );
};

/**
 * Memoized: its props are primitives, so a shell or layout change (including
 * every mousemove of a chat resize) does not re-render every mounted tab.
 */
const TabPane = React.memo<{ id: string; path: string; active: boolean }>(({ id, path, active }) => {
  const ref = useRef<HTMLDivElement>(null);
  // MemoryRouter reads its entries once; pin the mount-time path.
  const [initialPath] = useState(path);
  // A hidden (display:none) scroller loses its offset; keep the last one seen
  // while visible and put it back when the tab is shown again (spec §4).
  const scrollTop = useRef(0);
  useLayoutEffect(() => {
    if (active && ref.current) ref.current.scrollTop = scrollTop.current;
  }, [active]);
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (active) scrollTop.current = e.currentTarget.scrollTop;
  };
  return (
    <div
      ref={ref}
      data-testid={`shell-tab-pane-${id}`}
      hidden={!active}
      onScroll={onScroll}
      className="h-full overflow-auto"
    >
      <TabProvider tabId={id} isActive={active} rootRef={ref}>
        <DetachedRouter initialEntries={[initialPath]}>
          <TabLocationSync />
          <TabBody>
            <Routes>
              {SHELL_ROUTES.map((r) => <Route key={r.path} path={r.path} element={r.element} />)}
            </Routes>
          </TabBody>
        </DetachedRouter>
      </TabProvider>
    </div>
  );
});
TabPane.displayName = 'TabPane';

export const AppShell: React.FC = () => {
  const { tabs, activeTabId, activeContext } = useShell();
  const [layout, setLayout] = useState<Layout>(readLayout);
  const [view, setView] = useState<'chat' | 'workspace'>('workspace');
  const narrow = useNarrow();

  const update = useCallback((next: Layout) => {
    setLayout(next);
    writeLayout(next);
  }, []);

  const stopResize = useRef<(() => void) | null>(null);
  useEffect(() => () => stopResize.current?.(), []);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    let latest = layout.width;
    const move = (ev: MouseEvent) => {
      latest = clampWidth(window.innerWidth - ev.clientX);
      setLayout((l) => ({ ...l, width: latest }));
    };
    const detach = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      stopResize.current = null;
    };
    const up = () => {
      detach();
      writeLayout({ width: latest, collapsed: false });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    stopResize.current = detach;
  };

  const rail = layout.collapsed && !narrow;
  const chatStyle = narrow ? undefined : { width: rail ? RAIL_WIDTH : layout.width };
  const collapseBtn = narrow ? null : (
    <button
      type="button"
      aria-label="Collapse CEPI"
      data-testid="shell-chat-collapse"
      onClick={() => update({ ...layout, collapsed: true })}
      className="rounded p-1 text-ink-2 hover:bg-line"
    >
      <ChevronRight className="h-4 w-4" />
    </button>
  );

  return (
    <div data-testid="app-shell" className="flex h-screen w-full flex-col bg-ground lg:flex-row">
      {narrow && (
        <div data-testid="shell-view-toggle" className="flex border-b border-line">
          {(['chat', 'workspace'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={`flex-1 py-2 text-sm font-medium ${view === v ? 'text-accent' : 'text-ink-2'}`}
            >
              {v === 'chat' ? 'Chat' : 'Workspace'}
            </button>
          ))}
        </div>
      )}

      <section
        data-testid="shell-workspace-column"
        className={`${narrow && view !== 'workspace' ? 'hidden' : 'flex'} min-h-0 min-w-0 flex-1 flex-col`}
      >
        {/* No overflow-hidden here: the UserProfile dropdown is absolutely positioned. */}
        <header className="flex min-h-14 items-stretch gap-3 border-b border-line px-5">
          <div className="flex items-center">
            <BrandMark size="sm" />
          </div>
          <div className="flex min-w-0 flex-1 items-stretch">
            <TabStrip />
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <FeedbackButton variant="header" />
            <UserProfile />
          </div>
        </header>
        <div className="min-h-0 flex-1">
          {/* Home also covers "tabs exist but none is active": a background open (A3 CEPI run launch) from zero tabs. */}
          {!tabs.some((t) => t.id === activeTabId) && <div className="h-full overflow-auto"><Home /></div>}
          {tabs.map((t) => (
            <TabPane key={t.id} id={t.id} path={t.path} active={t.id === activeTabId} />
          ))}
        </div>
      </section>

      <aside
        data-testid="shell-chat-column"
        style={chatStyle}
        className={`${narrow && view !== 'chat' ? 'hidden' : 'flex'} relative min-h-0 shrink-0 flex-col border-l border-line bg-panel max-lg:w-full max-lg:flex-1`}
      >
        {rail && (
          <button
            type="button"
            aria-label="Expand CEPI"
            data-testid="shell-chat-expand"
            onClick={() => update({ ...layout, collapsed: false })}
            className="m-2 flex flex-col items-center gap-1 rounded p-1 text-ink-2 hover:bg-line"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="text-xs font-medium">CEPI</span>
          </button>
        )}
        {/* Collapsing only hides the slot: the chat keeps its state. */}
        <div hidden={rail} className="min-h-0 flex-1">
          <UnifiedChatPanel published={activeContext} headerAction={collapseBtn} />
        </div>
        {!narrow && !rail && (
          <div
            role="separator"
            aria-orientation="vertical"
            data-testid="shell-chat-resize"
            onMouseDown={startResize}
            className="absolute left-0 top-0 h-full w-1 cursor-col-resize hover:bg-accent-ring"
          />
        )}
      </aside>

      <CapPrompt />
    </div>
  );
};

export default AppShell;
